import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import {
  deleteUser,
  reauthenticateWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  writeBatch,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "./firebaseConfig";

const CONFIRM_PHRASE = "delete my account";

const DELETION_ITEMS = [
  "Your account and sign-in access",
  "All shortened links (random and custom)",
  "All click analytics data",
  "All folder organisation",
  "Your Pro subscription access",
];

export default function DeleteAccountDialog({ open, onClose, user, onDeleted }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset state every time the dialog opens
  useEffect(() => {
    if (open) {
      setText("");
      setError("");
      setLoading(false);
    }
  }, [open]);

  const isConfirmed = text.trim().toLowerCase() === CONFIRM_PHRASE;

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setLoading(true);
    setError("");

    try {
      // 1. Batch-delete all user links
      const [linksSnap, customSnap] = await Promise.all([
        getDocs(query(collection(db, "links"), where("uid", "==", user.uid))),
        getDocs(query(collection(db, "linksCustom"), where("uid", "==", user.uid))),
      ]);

      const allDocs = [
        ...linksSnap.docs,
        ...customSnap.docs,
      ];

      // Firestore batch limit is 500 writes
      for (let i = 0; i < allDocs.length; i += 499) {
        const batch = writeBatch(db);
        allDocs.slice(i, i + 499).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }

      // 2. Delete Firebase Auth user (re-auth if session is stale)
      const deleteAuthUser = async () => {
        try {
          await deleteUser(auth.currentUser);
        } catch (e) {
          if (e.code === "auth/requires-recent-login") {
            await reauthenticateWithPopup(
              auth.currentUser,
              new GoogleAuthProvider()
            );
            await deleteUser(auth.currentUser);
          } else {
            throw e;
          }
        }
      };

      await deleteAuthUser();

      // onAuthStateChanged in App.js will handle clearing user state
      onDeleted();
    } catch (e) {
      console.error("Account deletion error:", e);
      if (e.code === "auth/popup-closed-by-user") {
        setError("Re-authentication cancelled. Please try again.");
      } else {
        setError("Something went wrong. Please try again or contact support.");
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{ sx: { borderRadius: 5, width: "100%", maxWidth: 460, m: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 0.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.1rem">
          Delete Account
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: "12px !important" }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 3, mb: 2.5, fontSize: "0.85rem" }}
        >
          This action is <strong>permanent and irreversible.</strong> There is
          absolutely no way to recover your data once deleted.
        </Alert>

        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={600}
          sx={{ mb: 1 }}
        >
          The following will be permanently erased:
        </Typography>

        <List dense disablePadding sx={{ mb: 2.5 }}>
          {DELETION_ITEMS.map((item) => (
            <ListItem key={item} disableGutters sx={{ py: 0.3 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <RemoveCircleOutlineRoundedIcon
                  sx={{ fontSize: 15, color: "error.main" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{ fontSize: "0.85rem" }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mb: 2.5 }} />

        <Typography variant="body2" sx={{ mb: 1 }}>
          To confirm, type{" "}
          <Box
            component="span"
            sx={{
              fontFamily: "monospace",
              fontWeight: 700,
              bgcolor: "action.selected",
              px: 0.75,
              py: 0.2,
              borderRadius: 1,
              fontSize: "0.85rem",
            }}
          >
            delete my account
          </Box>{" "}
          below:
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="delete my account"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={error}
          autoComplete="off"
          spellCheck={false}
          disabled={loading}
          sx={{ "& input": { fontFamily: "monospace" } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          variant="text"
          onClick={onClose}
          disabled={loading}
          sx={{ borderRadius: 50, color: "text.secondary" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!isConfirmed || loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ borderRadius: 50, fontWeight: 700 }}
        >
          {loading ? "Deleting…" : "Permanently delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
